import { vm } from "../socket/config.js";
import {
    sendMessageToServer,
    sendChangeToServer,
    updateVersion,
} from "../socket/events.js";
import {
    targetToName,
    nameToTarget,
    isBadToSend,
    isBadToRun,
    isBadToRunBlockly,
    isVariableExistsGlobally,
} from "../utils/scratch-helpers.js";
import { isWorkspaceAccesable, getWorkspace } from "../utils/dom.js";
import {
    pauseWorkspaceUpdate,
    resumeWorkspaceUpdate,
    isLoadingProject,
    isPauseEventHandling,
} from "../vm/overrides.js";
import { setHighlightBlockStyle } from "./styles.js";

let ScratchBlocks = null;
let blocklyWorkspace = null;
let draggingBlock = null;
let previewBlock = null;
let currentSelected = null;
let selectBlockMap = new Map();
let ignoreNextMove = new Set();

const pendingEchoes = new Map();
const ECHO_WINDOW_MS = 250;

const echoKey = (e) =>
    `${e.type}:${e.blockId ?? e.varId ?? e.commentId ?? ""}`;

const expectEcho = (e) => {
    const key = echoKey(e);
    pendingEchoes.set(key, (pendingEchoes.get(key) ?? 0) + 1);
    setTimeout(() => {
        const left = (pendingEchoes.get(key) ?? 0) - 1;
        if (left > 0) pendingEchoes.set(key, left);
        else pendingEchoes.delete(key);
    }, ECHO_WINDOW_MS);
};

const consumeEcho = (e) => {
    const key = echoKey(e);
    const left = pendingEchoes.get(key);
    if (!left) return false;

    if (left > 1) pendingEchoes.set(key, left - 1);
    else pendingEchoes.delete(key);
    return true;
};
let etuListeners = [];
let lastEditPositionSent = 0;
const EDIT_POSITION_THROTTLE_MS = 300;

export const setScratchBlocks = (sb) => {
    ScratchBlocks = sb;
};
export const getScratchBlocks = () => ScratchBlocks;
export const setBlocklyWorkspace = (bw, { syncChanges = true } = {}) => {
    blocklyWorkspace = bw;
    if (syncChanges) blocklyWorkspace.addChangeListener(onBlocklyEvent);
};
export const getBlocklyWorkspace = () => blocklyWorkspace;

export const withBlocklyEventsDisabled = (fn) => {
    const events = ScratchBlocks?.Events;
    if (!events?.disable) return fn();

    events.disable();
    try {
        return fn();
    } finally {
        events.enable();
    }
};

export const broadcastEditPosition = (blockId) => {
    if (!blockId) return;
    const block = getBlock(blockId);
    if (!block) return;

    const now = Date.now();
    if (now - lastEditPositionSent < EDIT_POSITION_THROTTLE_MS) return;
    lastEditPositionSent = now;

    const pos = block.getRelativeToSurfaceXY();
    const size = block.getHeightWidth();

    sendMessageToServer("editPosition", {
        target: targetToName(vm.editingTarget),
        blockId,
        x: pos.x + size.width / 2,
        y: pos.y + size.height / 2,
    });
};

export const onBlocklyEvent = async (e) => {
    if (isLoadingProject() || !e?.type) return;

    if (e.type === ScratchBlocks.Events.UI && e.element === "stackclick") {
        sendMessageToServer("stackClicked", {
            blockId: e.blockId,
            target: targetToName(vm.editingTarget),
        });
        return;
    }

    if (e.type === ScratchBlocks.Events.UI && e.element === "selected") {
        if (isLiveBlock(draggingBlock) && draggingBlock.id === e.newValue) {
            blocklyWorkspace?.cancelCurrentGesture?.();
            return;
        }

        const msg = { blockId: e.newValue };

        if (currentSelected && currentSelected.id === e.newValue) {
            resetBlock(currentSelected.id);
        }

        sendMessageToServer("blockSelected", msg);
        if (e.newValue) broadcastEditPosition(e.newValue);
        return;
    }

    if (e.type === "endDrag") {
        sendMessageToServer("endDrag", e.blockId);
    }

    if (
        ![
            "create",
            "delete",
            "move",
            "change",
            "comment_create",
            "comment_delete",
            "comment_move",
            "comment_change",
            "var_create",
            "var_delete",
            "var_change",
            "var_rename",
        ].includes(e.type)
    ) {
        return;
    }

    if (e.type === "move" && e.blockId && ignoreNextMove.has(e.blockId)) {
        ignoreNextMove.delete(e.blockId);
        return;
    }

    if (consumeEcho(e)) return;

    try {
        let extrargs = {};

        if (!!e.xml) {
            extrargs.xml = { outerHTML: e.xml.outerHTML };
            extrargs.isCBCreateOrDelete =
                e.xml?.getAttribute("type") == "procedures_definition";
        }

        if (!!e.oldXml) {
            extrargs.isCBCreateOrDelete =
                extrargs.isCBCreateOrDelete ||
                e.oldXml?.getAttribute("type") == "procedures_definition";
        }

        const serializedEvent = {
            meta: "vm.blockListen",
            type: e.type,
            json: e.toJson(),
            target: targetToName(vm.editingTarget),
            event: e,
            extrargs,
            metrics: {},
        };

        const block = getBlock(e.blockId);

        if (e.type === "create") {
            if (isBadToSend(e, vm.editingTarget)) {
                return;
            }

            serializedEvent.metrics = {
                blockId: e.blockId,
                blockType: block.type,
                chain: getBlockChain(block),
                json: serializedEvent.json,
            };
            sendChangeToServer(serializedEvent);
            broadcastEditPosition(e.blockId);
            return;
        }

        if (block) {
            if (e.type === "move") {
                let pos = e.newCoordinate;
                let oldPos = e.oldCoordinate;

                if (pos === undefined) pos = block.getRelativeToSurfaceXY();
                if (oldPos === undefined)
                    oldPos = block.getRelativeToSurfaceXY();

                serializedEvent.metrics = {
                    blockId: e.blockId,
                    blockType: block.type,
                    oldX: oldPos.x,
                    oldY: oldPos.y,
                    newX: pos.x,
                    newY: pos.y,
                    chain: getBlockChain(block),
                    json: serializedEvent.json,
                };
            }

            if (e.type === "change") {
                serializedEvent.metrics = {
                    blockId: e.blockId,
                    blockType: block.type,
                    oldValue: e.oldValue,
                    newValue: e.newValue,
                    json: serializedEvent.json,
                };
            }
        } else {
            if (e.type === "delete")
                serializedEvent.metrics = { blId: e.blockId };
        }

        if (isBadToSend(e, vm.editingTarget)) {
            return;
        }
        if (e.type === "move" && !!draggingBlock) return;

        sendChangeToServer(serializedEvent);
        broadcastEditPosition(e.blockId);
        updateVersion(vm);
    } catch (e) {
        console.error("Error sending block event:", e);
    }
};

export const applyRemoteChange = async (data) => {
    if (data.msg.meta === "vm.blockListen") {
        await onBlockEventReceived(data);
    }
};

export const onBlockEventReceived = async (data) => {
    if (!data?.msg) return;
    const msg = data.msg;
    if (
        msg.type === "var_create" &&
        !msg.json.isLocal &&
        isVariableExistsGlobally(msg.json.varName)
    )
        return;

    let oldEditingTarget = vm.editingTarget;
    vm.editingTarget = nameToTarget(msg.target);
    vm.runtime._editingTarget = vm.editingTarget;

    pauseWorkspaceUpdate();

    try {
        let bEvent = ScratchBlocks.Events.fromJson(msg.json, blocklyWorkspace);
        let vEvent = msg.event;

        if (msg.type === "comment_change") {
            bEvent.newContents_ = vEvent.newContents_;
            bEvent.oldContents_ = vEvent.oldContents_;
        }

        if (!!msg.extrargs.xml) {
            vEvent.xml = msg.extrargs.xml;
        }

        vEvent.type = msg.type;

        if (
            ["var_create", "var_rename", "var_delete", "var_change"].includes(
                msg.type,
            ) &&
            !isBadToRun(vEvent, vm.editingTarget)
        ) {
            vm.editingTarget.blocks.blocklyListen(vEvent);
        }

        if (
            targetToName(oldEditingTarget) === msg.target &&
            isWorkspaceAccesable() &&
            !isPauseEventHandling()
        ) {
            if (bEvent.type === "create") {
                if (bEvent.blockId) {
                    ignoreNextMove.add(bEvent.blockId);
                    setTimeout(
                        () => ignoreNextMove.delete(bEvent.blockId),
                        200,
                    );
                }
            }

            if (
                !isBadToRun(bEvent, vm.editingTarget) &&
                !isBadToRunBlockly(bEvent, blocklyWorkspace)
            ) {
                expectEcho(bEvent);
                bEvent.run(true);
            }

            if (selectBlockMap.has(bEvent.blockId)) {
                onBlockSelected(selectBlockMap.get(bEvent.blockId));
                selectBlockMap.delete(bEvent.blockId);
            }
        } else {
            if (!isBadToRun(vEvent, vm.editingTarget)) {
                vm.editingTarget.blocks.blocklyListen(vEvent);
            }
        }
    } catch (e) {
        console.error("Error on block event execution", e);
    }

    if (msg.extrargs?.isCBCreateOrDelete) {
        blocklyWorkspace?.refreshToolboxSelection_?.();
    }

    if (!!oldEditingTarget && !!vm.runtime.getTargetById(oldEditingTarget.id)) {
        vm.editingTarget = oldEditingTarget;
        vm.runtime._editingTarget = oldEditingTarget;
    }

    resumeWorkspaceUpdate();
};

// Drag preview interval
setInterval(() => {
    if (Blockly.getMainWorkspace().isDragging() !== null) {
        const tempBlock = blocklyWorkspace
            .getBlockDragSurface()
            ?.getCurrentBlock();
        const draggedBlock = getBlock(tempBlock?.getAttribute("data-id"));

        if (draggedBlock) {
            const msg = {
                type: draggedBlock.type,
                id: draggedBlock.id,
                position: draggedBlock.getRelativeToSurfaceXY(),
                target: targetToName(vm.editingTarget),
            };

            sendMessageToServer("previewPosition", msg);
            broadcastEditPosition(draggedBlock.id);

            if (currentSelected && currentSelected.id === draggedBlock.id) {
                resetBlock(currentSelected.id);
            }
        }
    }
}, 150);

const isLiveBlock = (block) => !!block && !!block.workspace;

export const showPreviewBlock = async (data) => {
    if (!isLiveBlock(draggingBlock) || draggingBlock.id !== data.data.id) {
        draggingBlock = getBlock(data.data.id);
    }

    if (!isLiveBlock(draggingBlock)) {
        draggingBlock = null;
        return;
    }

    if (data.data.target !== targetToName(vm.editingTarget)) return;

    try {
        let childBlock;
        if (draggingBlock.isMovable()) {
            draggingBlock.setMovable(false);
            childBlock = draggingBlock.childBlocks_.find(
                (b) => b.category_ !== null,
            );
            while (childBlock) {
                childBlock.setMovable(false);
                childBlock = childBlock.childBlocks_.find(
                    (b) => b.category_ !== null,
                );
            }
        }
        if (draggingBlock.isDeletable()) {
            draggingBlock.setDeletable(false);
            childBlock = draggingBlock.childBlocks_.find(
                (b) => b.category_ !== null,
            );
            while (childBlock) {
                childBlock.setDeletable(false);
                childBlock = childBlock.childBlocks_.find(
                    (b) => b.category_ !== null,
                );
            }
        }

        const currentPos = draggingBlock.getRelativeToSurfaceXY();
        const targetX = data.data.position.x;
        const targetY = data.data.position.y;

        const dx = targetX - currentPos.x;
        const dy = targetY - currentPos.y;

        draggingBlock.moveBy(dx, dy);
    } catch (e) {
        console.error("Error showing preview block:", e);
        draggingBlock = null;
    }
};

export const onEndDrag = () => {
    try {
        if (isLiveBlock(draggingBlock)) {
            let childBlock;
            if (!draggingBlock.isMovable()) {
                draggingBlock.setMovable(true);
                childBlock = draggingBlock.childBlocks_.find(
                    (b) => b.category_ !== null,
                );
                while (childBlock) {
                    childBlock.setMovable(true);
                    childBlock = childBlock.childBlocks_.find(
                        (b) => b.category_ !== null,
                    );
                }
            }
            if (!draggingBlock.isDeletable()) {
                draggingBlock.setDeletable(true);
                childBlock = draggingBlock.childBlocks_.find(
                    (b) => b.category_ !== null,
                );
                while (childBlock) {
                    childBlock.setDeletable(true);
                    childBlock = childBlock.childBlocks_.find(
                        (b) => b.category_ !== null,
                    );
                }
            }
        }
    } catch (e) {
        console.error("Error restoring block after drag:", e);
    } finally {
        draggingBlock = null;
    }

    if (previewBlock) {
        try {
            previewBlock.dispose();
        } catch (e) {
            console.error("Error disposing preview block:", e);
        }
        previewBlock = null;
    }
};

export const onBlockSelected = (data) => {
    const blockId = data.data.blockId;

    if (currentSelected && currentSelected.id !== blockId) {
        resetBlock(currentSelected.id);
    }

    if (blockId === null) return;

    const block = getBlock(blockId);
    if (!block) {
        selectBlockMap.set(blockId, data);
        return;
    }

    const svgRoot = block.getSvgRoot();
    if (!svgRoot) return;
    svgRoot.style.transition = "transform 0.5s";
    setHighlightBlockStyle(svgRoot, true, data.username, data.color);
    currentSelected = block;
};

export const resetBlock = (blockId) => {
    const block = getBlock(blockId);
    if (!block) return;

    let svgRoot = block.getSvgRoot();
    if (svgRoot) {
        svgRoot.classList.remove(
            "turnOn",
            "turnedOn",
            "turnOff",
            "turnedOff",
            "blocRect",
        );

        const tags = svgRoot.querySelectorAll(".blockly-name-tag");
        tags.forEach((tag) => tag.remove());
        if (svgRoot.getAnimations())
            svgRoot.getAnimations().forEach((anim) => anim.cancel());

        svgRoot.style.outline = "";
        svgRoot.style.outlineColor = "";
        svgRoot.style.transition = "";
        svgRoot.style.filter = "";
    }

    if (currentSelected && block.id === currentSelected.id)
        currentSelected = null;
};

export const onStackClicked = (data) => {
    const target = nameToTarget(data.data?.target);
    if (!target) return;

    vm.runtime.toggleScript(data.data.blockId, { target, stackClick: true });
};

export const getBlockChain = (block) => {
    let result = "";
    let parentBlock = block.parentBlock_;
    if (parentBlock === null) return block.type;

    while (parentBlock != null) {
        let next = parentBlock.parentBlock_;
        if (next !== null) parentBlock = parentBlock.parentBlock_;
        else break;
    }

    result += parentBlock.type + " - ";
    let childBlock = parentBlock.childBlocks_[1];
    while (childBlock) {
        result += childBlock.type;
        childBlock = childBlock.childBlocks_[1];
        if (childBlock) result += " - ";
    }

    return result;
};

export function getBlock(id) {
    if (!isWorkspaceAccesable()) return null;
    return blocklyWorkspace.getBlockById(id);
}
